import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useTracking } from 'react-tracking';
import { format } from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { CatGrid } from '@blocks-react/components/Grid';
import { CatButton } from '@blocks-react/components/Button';
import { CatGridItem } from '@blocks-react/components/GridItem';
import { loadSavedListDetails } from '@app/store/account/actions';
import { CatInputField } from '@blocks-react/components/InputField';
import { CatDaterangepickerField } from '@blocks-react/components/DaterangepickerField';
import { SAVED_LIST_FIELDS, DATE_FORMAT } from '../constants';
import {
  getSearchResultsFormEvent,
  gaSearchValidationError
} from '../analyticsUtils';

import { DATE_FNS_LOCALES } from '@app/constants/languageConstants';
import { BUTTON_VARIANTS } from '@app/constants/commonConstants';

const SearchOptions = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const locale = useSelector(s => s.common?.locale);
  const dealerDateFormat = useSelector(s => s.dealer.dealerDateFormat);
  const { trackEvent } = useTracking();
  const searchOptionsSavedList = useSelector(s => s.account.searchOptions);
  const { NAME, ITEM_PART_NUMBER, CREATED_BY, DATE_VALUE } = SAVED_LIST_FIELDS;
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { isSubmitting }
  } = useForm({
    defaultValues: {
      [NAME]: '',
      [ITEM_PART_NUMBER]: '',
      [CREATED_BY]: '',
      [DATE_VALUE]: { startDate: '', endDate: '' }
    }
  });
  const onFailGAEvent = data => {
    return trackEvent(
      gaSearchValidationError({
        itemPartNumber: data?.itemPartNumber,
        startDate: data?.startDate,
        endDate: data?.endDate
      })
    );
  };

  const onSuccessGAEvent = data => {
    trackEvent(
      getSearchResultsFormEvent({
        itemPartNumber: data?.itemPartNumber,
        startDate: data?.startDate,
        endDate: data?.endDate,
        savedListsLength: data.savedLists?.length
      })
    );
  };

  const handleSearch = data => {
    const { folName, itemPartNumber, createdBy, dateValue } = data;
    dispatch(
      loadSavedListDetails(
        {
          ...searchOptionsSavedList,
          folName: folName?.trim() || '',
          itemPartNumber: itemPartNumber?.trim() || '',
          createdBy: createdBy?.trim() || '',
          startDate: dateValue.startDate || '',
          endDate: dateValue.endDate || ''
        },
        null,
        null,
        onSuccessGAEvent,
        onFailGAEvent
      )
    );
  };

  return (
    <form onSubmit={handleSubmit(handleSearch)}>
      <div>
        <CatGrid variant="2up" gap="none">
          <CatGridItem className="pe-md-3 pb-3">
            <CatInputField
              {...register(SAVED_LIST_FIELDS.NAME)}
              label={t('SAVED_LIST_NAME')}
            />
          </CatGridItem>
          <CatGridItem className="pe-md-0 pb-3">
            <CatInputField
              {...register(SAVED_LIST_FIELDS.ITEM_PART_NUMBER)}
              label={t('ITEM_NUMBER')}
            />
          </CatGridItem>
        </CatGrid>
      </div>
      <div>
        <CatGrid variant="2up" gap="none">
          <CatGridItem className="pe-md-3 pb-3">
            <CatInputField
              {...register(SAVED_LIST_FIELDS.CREATED_BY)}
              label={t('REQUISITIONLIST_CREATOR')}
            />
          </CatGridItem>
          <CatGridItem>
            <CatDaterangepickerField
              isInline="true"
              isFullWidth
              {...register(SAVED_LIST_FIELDS.DATE_VALUE)}
              dateFormat={dealerDateFormat || DATE_FORMAT}
              data-testid="dateFormat"
              onBlDateRangeChange={e => {
                const startDate = e.detail.start.value ?? '';
                const endDate = e.detail.end.value ?? '';
                const formattedStartDate = startDate
                  ? format(parseISO(startDate), dealerDateFormat || DATE_FORMAT)
                  : '';
                const formattedEndDate = endDate
                  ? format(parseISO(endDate), dealerDateFormat || DATE_FORMAT)
                  : '';
                setValue(SAVED_LIST_FIELDS.DATE_VALUE, {
                  startDate: formattedStartDate,
                  endDate: formattedEndDate
                });
              }}
              startDateLabel={t('CAT_START_DATE')}
              endDateLabel={t('CAT_END_DATE')}
              setActiveStartDate={watch(SAVED_LIST_FIELDS.DATE_VALUE).startDate}
              setActiveEndDate={watch(SAVED_LIST_FIELDS.DATE_VALUE).endDate}
              placeholder={t('SELECT_DATE')}
              locale={DATE_FNS_LOCALES[locale]}
            />
          </CatGridItem>
        </CatGrid>
        <div className="d-flex justify-content-end pt-4 pb-2">
          <CatButton
            className="cat-u-padding-right-sm"
            variant={BUTTON_VARIANTS.GHOST}
            onClick={() => reset()}
          >
            {t('CLEAR_FILTERS')}
          </CatButton>
          <CatButton type="submit" disabled={isSubmitting}>
            {t('APPROVAL_BTN_SEARCH')}
          </CatButton>
        </div>
      </div>
    </form>
  );
};

export default SearchOptions;
